/**
 * usePOSPrint - Custom hook for POS print logic
 * Extracted from POSPage.jsx to reduce component complexity.
 * Supports iMin, Bluetooth, QZ Tray (Windows), and browser fallback printing.
 */
import { toast } from "sonner";
import { api } from "@/store/useStore";
import { bluetoothPrinter } from "@/lib/bluetooth-printer";
import {
  generateReceiptHTML,
  generateKitchenTicketHTML,
  getPaperDimensions,
  openPrintWindow,
  generateShiftSummaryHTML,
} from "@/lib/print-utils";
import iminPrinter, {
  isIminDevice,
  printReceipt as printReceiptImin,
  printKitchenTicketImin,
} from "@/services/iminPrinter";
import qzPrinter from "@/services/qzTrayPrinter";
import { printerManager } from "@/lib/printer-manager";

/**
 * @param {Object} opts
 * @param {Object} opts.currentDevice - Registered POS device
 * @param {Array}  opts.printDestinations - Configured print destinations for outlet
 * @param {boolean} opts.isImin - Whether running on iMin device
 * @param {Object} opts.invoiceTemplate - Current invoice template settings
 * @param {Object} opts.currentOutlet - Active outlet
 * @param {Object} opts.user - Authenticated user
 */
export function usePOSPrint({
  currentDevice,
  printDestinations,
  isImin,
  invoiceTemplate,
  currentOutlet,
  user,
}) {
  // ─── Resolve printer type & paper size for a destination ─────────────
  const resolvePrinter = (destination, destConfig) => {
    let printerType = "browser";
    let paperSize = "80mm";

    if (destConfig) {
      printerType = destConfig.printer_type || "browser";
      paperSize = destConfig.paper_size || "80mm";
    } else if (currentDevice) {
      printerType = currentDevice.printer_connection || "auto";
      paperSize = currentDevice.builtin_paper_size || "80mm";
    } else {
      const saved = localStorage.getItem("printSettings");
      const settings = saved ? JSON.parse(saved) : {};
      if (destination === "receipt" && settings.receiptPrinter === "bluetooth") printerType = "bluetooth";
      else if (destination === "kitchen" && settings.kitchenPrinter === "bluetooth") printerType = "bluetooth";
      else if (destination === "bar" && settings.barPrinter === "bluetooth") printerType = "bluetooth";
    }
    return { printerType, paperSize };
  };

  // ─── Generate print HTML for browser fallback ────────────────────────
  const generatePrintHTMLLocal = (data, destination) => {
    const destConfig = printDestinations.find(
      (d) => d.destination_type === destination
    );
    const paperSize =
      destConfig?.paper_size || invoiceTemplate?.paper_size || "80mm";
    const dims = getPaperDimensions(paperSize);

    if (
      data.type === "kitchen_ticket" ||
      destination === "kitchen" ||
      destination === "bar"
    ) {
      return generateKitchenTicketHTML(data, dims, destConfig);
    }
    return generateReceiptHTML(data, invoiceTemplate || {});
  };

  // ─── Browser print helper ───────────────────────────────────────────
  const printReceiptBrowser = async (data, reservedWindow = null) => {
    const template = data.template || invoiceTemplate || {};
    const receiptHtml = generateReceiptHTML(data, template);
    const opened = openPrintWindow(
      receiptHtml,
      `Receipt - ${data.invoice_number || data.order_number}`,
      reservedWindow
    );
    if (!opened) {
      throw new Error("Your browser blocked the receipt window. Allow pop-ups for this POS site and try again.");
    }
    return true;
  };

  const closeReservedReceiptWindow = (receiptWindow) => {
    if (receiptWindow && !receiptWindow.closed) receiptWindow.close();
  };

  // Must run synchronously from the Pay button click. Opening a new receipt
  // window after the close/eTIMS API calls finish loses the browser's user
  // activation and the print dialog never appears.
  const reserveBrowserReceiptWindow = () => {
    const hasIminReceiptPrinter = isImin && ["auto", "builtin"].includes(
      currentDevice?.printer_connection || ""
    );
    const hasBluetoothReceiptPrinter =
      (currentDevice?.printer_connection === "bluetooth" && bluetoothPrinter.isConnected) ||
      (!currentDevice && bluetoothPrinter.isConnected);
    const hasWindowsReceiptPrinter = printDestinations.some(
      (destination) =>
        destination.destination_type === "receipt" &&
        destination.printer_type === "windows" &&
        destination.auto_print &&
        destination.windows_printer_name
    );

    if (hasIminReceiptPrinter || hasBluetoothReceiptPrinter || hasWindowsReceiptPrinter) {
      return null;
    }

    const receiptWindow = window.open("", "_blank", "width=300,height=600");
    if (receiptWindow) {
      receiptWindow.document.write("<title>Preparing receipt</title><p>Preparing receipt...</p>");
      receiptWindow.document.close();
    }
    return receiptWindow;
  };

  // ─── Auto-print after settlement ───────────────────────────────────
  const handleAutoPrint = async (printData, reservedWindow = null) => {
    if (!printData) {
      closeReservedReceiptWindow(reservedWindow);
      return false;
    }

    try {
      const template = printData.template || invoiceTemplate || {};
      let printerType = "browser";
      let paperSize = template.paper_size || "80mm";

      if (currentDevice) {
        printerType = currentDevice.printer_connection || "auto";
        paperSize =
          template.paper_size ||
          currentDevice.builtin_paper_size ||
          "80mm";
      } else {
        const saved = localStorage.getItem("printSettings");
        const settings = saved ? JSON.parse(saved) : {};
        if (settings.receiptPrinter === "bluetooth") printerType = "bluetooth";
      }

      // iMin device
      if (
        (printerType === "auto" || printerType === "builtin") &&
        isImin
      ) {
        try {
          const receiptForImin = {
            logoUrl:
              template.logo_base64 || template.logo_url || printData.logo_url,
            logoWidth: template.logo_width || 150,
            logoHeight: template.logo_height || 60,
            businessName: printData.business_name,
            businessAddress: printData.business_address,
            businessPhone: printData.business_phone,
            kraPin: printData.kra_pin,
            invoiceNumber:
              printData.invoice_number || printData.order_number,
            date: printData.date,
            time: new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            }),
            serverName:
              template.show_server_name !== false
                ? printData.served_by
                : null,
            tableName:
              template.show_table_number !== false
                ? printData.table_number
                : null,
            orderType:
              template.show_order_type !== false
                ? printData.order_type
                : null,
            customerName:
              template.show_customer_details !== false
                ? printData.customer_name
                : null,
            items: printData.items,
            groupByCategory: template.group_products_by_category,
            subtotal: printData.subtotal,
            tax: printData.tax_total || printData.tax,
            showDetailedTaxes: template.show_detailed_taxes,
            taxBreakdown: printData.tax_breakdown || [],
            serviceCharge: printData.service_charge,
            discount: printData.discount,
            total: printData.total,
            paymentMethod:
              template.show_payment_method !== false
                ? printData.payment_method
                : null,
            mpesaReceipt: printData.mpesa_receipt,
            pointsEarned:
              template.show_loyalty_points !== false
                ? printData.points_earned
                : 0,
            headerItems: template.header_items || [],
            footerItems: template.footer_items || [],
            labels: template.labels || {},
            showQrCode: template.show_qr_code,
            qrCodeUrl: template.qr_code_url,
            etims: printData.etims,
          };
          await printReceiptImin(receiptForImin, paperSize);
          closeReservedReceiptWindow(reservedWindow);
          toast.success(`Receipt printed (${paperSize})`);
          return true;
        } catch (_) {
          // fall through
        }
      }

      // Bluetooth
      if (printerType === "bluetooth" && bluetoothPrinter.isConnected) {
        try {
          await bluetoothPrinter.printReceipt({
            ...printData,
            showDetailedTaxes: template.show_detailed_taxes,
            taxBreakdown: printData.tax_breakdown || [],
            etims: printData.etims,
          });
          closeReservedReceiptWindow(reservedWindow);
          toast.success("Receipt printed with eTIMS QR code");
          return true;
        } catch (_) {
          // fall through
        }
      }

      // QZ Tray auto-print for receipt destination
      const receiptDest = printDestinations.find(
        (d) =>
          d.destination_type === "receipt" &&
          d.printer_type === "windows" &&
          d.auto_print
      );

      if (receiptDest?.windows_printer_name) {
        try {
          const connected = await qzPrinter.connect();
          if (connected) {
            const receiptData = {
              businessName:
                printData.property_name || printData.business_name,
              businessAddress:
                printData.property_address || printData.business_address,
              businessPhone:
                printData.property_phone || printData.business_phone,
              invoiceNumber:
                printData.invoice_number || printData.order_number,
              tableNumber: printData.table_number,
              waiterName: printData.served_by,
              customerName: printData.customer_name,
              items: printData.items,
              subtotal: printData.subtotal,
              tax: printData.tax,
              taxBreakdown: printData.tax_breakdown,
              discount: printData.discount,
              total: printData.total,
              paymentMethod: printData.payment_method,
              amountPaid: printData.amount_paid,
              changeAmount: printData.change_amount,
              isReprint: printData.print_count > 1,
              printCount: printData.print_count,
              etims: printData.etims,
              kraPin: printData.kra_pin,
              template: template || {},
            };
            await qzPrinter.printReceipt(
              receiptDest.windows_printer_name,
              receiptData,
              receiptDest.paper_size || paperSize
            );
            closeReservedReceiptWindow(reservedWindow);
            toast.success(
              `Receipt printed to ${receiptDest.windows_printer_name}`
            );
            return true;
          }
        } catch (_) {
          // fall through
        }
      }

      // Browser fallback
      return await printReceiptBrowser({ ...printData, template }, reservedWindow);
    } catch (_) {
      closeReservedReceiptWindow(reservedWindow);
      // auto-print failure shouldn't block the flow
      return false;
    }
  };

  // ─── Print invoice by ID (receipt / kitchen / bar) ──────────────────
  const handlePrintInvoice = async (invoiceId, destination = "receipt") => {
    try {
      const result = await api.post(
        `/invoices/${invoiceId}/print?destination=${destination}`
      );
      const printData = result.print_data;

      const destConfig = printDestinations.find(
        (d) =>
          d.destination_type === destination ||
          (destination === "receipt" && d.destination_type === "receipt") ||
          (destination === "kitchen" && d.destination_type === "kitchen") ||
          (destination === "bar" && d.destination_type === "bar")
      );

      const { printerType, paperSize } = resolvePrinter(
        destination,
        destConfig
      );

      // iMin
      if (
        (printerType === "auto" || printerType === "builtin") &&
        isImin
      ) {
        try {
          if (printData.type === "receipt") {
            const receiptForImin = {
              logoUrl:
                invoiceTemplate?.logo_base64 ||
                printData.logo_url ||
                invoiceTemplate?.logo_url,
              businessName: printData.property_name,
              businessAddress: printData.property_address,
              headerItems: invoiceTemplate?.header_items || [],
              invoiceNumber:
                printData.invoice_number || printData.order_number,
              date: printData.date,
              time: new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              }),
              serverName: printData.served_by,
              tableName: printData.table_number,
              customerName: printData.customer_name,
              items: printData.items,
              groupByCategory: invoiceTemplate?.group_by_category,
              groupedItems: printData.grouped_items,
              subtotal: printData.subtotal,
              tax: printData.tax,
              showDetailedTaxes: invoiceTemplate?.show_detailed_taxes,
              taxBreakdown: printData.tax_breakdown,
              discount: printData.discount,
              total: printData.total,
              paymentMethod: printData.payment_method,
              pointsEarned: printData.points_earned || 0,
              footerItems: invoiceTemplate?.footer_items || [],
              showQrCode: invoiceTemplate?.show_qr_code,
              qrCodeUrl: invoiceTemplate?.qr_code_url,
              labels: invoiceTemplate?.labels || {},
              isReprint: printData.is_reprint,
              etims: printData.etims,
            };
            await printReceiptImin(receiptForImin, paperSize);
            toast.success(`Receipt printed (iMin ${paperSize})`);
          } else {
            const ticketForImin = {
              destination:
                printData.destination || destination.toUpperCase(),
              outletName: printData.outlet_name || currentOutlet?.name,
              tableNumber: printData.table_number,
              orderNumber: printData.order_number,
              orderType: printData.order_type,
              waiterName:
                printData.waiter_name ||
                printData.served_by ||
                user?.full_name ||
                user?.username,
              items: printData.items,
              isReprint: printData.is_reprint,
            };
            await printKitchenTicketImin(ticketForImin, paperSize);
            toast.success(`${destination} ticket printed (iMin)`);
          }
          return;
        } catch (_) {
          // fall through
        }
      }

      // Bluetooth
      if (printerType === "bluetooth") {
        const btPrinterName =
          currentDevice?.bluetooth_printer_name ||
          destConfig?.bluetooth_name;

        if (bluetoothPrinter.isConnected || btPrinterName) {
          try {
            if (printData.type === "receipt") {
              await bluetoothPrinter.printReceipt({
                businessName: printData.property_name,
                address: printData.property_address,
                phone: printData.property_phone,
                receiptNumber:
                  printData.invoice_number || printData.order_number,
                date: printData.date,
                tableNumber: printData.table_number,
                customerName: printData.customer_name,
                servedBy: printData.served_by,
                items: printData.items,
                subtotal: printData.subtotal,
                taxes: printData.tax_breakdown,
                tax: printData.tax,
                discount: printData.discount,
                total: printData.total,
                paymentMethod: printData.payment_method,
                mpesaReceipt: printData.mpesa_receipt,
                isReprint: printData.is_reprint,
                footer: "Thank you for dining with us!",
              });
            } else {
              await bluetoothPrinter.printKitchenTicket({
                destination: printData.destination,
                outletName: printData.outlet_name || currentOutlet?.name,
                orderNumber: printData.order_number,
                tableNumber: printData.table_number,
                orderType: printData.order_type,
                waiterName:
                  printData.waiter_name ||
                  printData.served_by ||
                  user?.full_name ||
                  user?.username,
                items: printData.items,
                isReprint: printData.is_reprint,
              });
            }
            toast.success(`Printed to ${destination} (Bluetooth)`);
            return;
          } catch (_) {
            // fall through
          }
        }
      }

      // QZ Tray (Windows)
      if (
        printerType === "windows" &&
        destConfig?.windows_printer_name
      ) {
        try {
          const connected = await qzPrinter.connect();
          if (connected) {
            if (destination === "kitchen" || destination === "bar") {
              const ticketData = {
                destination: destination.toUpperCase(),
                outletName:
                  currentOutlet?.name || printData.outlet_name,
                tableNumber: printData.table_number,
                orderNumber:
                  printData.order_number || printData.invoice_number,
                orderType: printData.order_type,
                waiterName:
                  printData.waiter_name ||
                  printData.served_by ||
                  user?.full_name,
                items: printData.items,
                isReprint: printData.print_count > 1,
              };
              await qzPrinter.printKitchenTicket(
                destConfig.windows_printer_name,
                ticketData,
                paperSize
              );
            } else {
              const receiptData = {
                businessName:
                  printData.property_name || printData.business_name,
                businessAddress:
                  printData.property_address ||
                  printData.business_address,
                businessPhone:
                  printData.property_phone || printData.business_phone,
                invoiceNumber:
                  printData.invoice_number || printData.order_number,
                tableNumber: printData.table_number,
                waiterName: printData.served_by,
                customerName: printData.customer_name,
                items: printData.items,
                subtotal: printData.subtotal,
                tax: printData.tax,
                taxBreakdown: printData.tax_breakdown,
                discount: printData.discount,
                total: printData.total,
                paymentMethod: printData.payment_method,
                amountPaid: printData.amount_paid,
                changeAmount: printData.change_amount,
                isReprint: printData.print_count > 1,
                printCount: printData.print_count,
                etims: printData.etims,
                kraPin: printData.kra_pin,
                template: invoiceTemplate || {},
              };
              await qzPrinter.printReceipt(
                destConfig.windows_printer_name,
                receiptData,
                paperSize
              );
            }
            toast.success(
              `Printed to ${destConfig.windows_printer_name} (QZ Tray)`
            );
            return;
          }
        } catch (_) {
          // fall through
        }
      }

      // Browser fallback
      const printWindow = window.open("", "_blank", "width=300,height=600");
      if (printWindow) {
        const content = generatePrintHTMLLocal(printData, destination);
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      }
      toast.success(result.message);
    } catch (error) {
      toast.error(error.message || "Print failed");
    }
  };

  // ─── Print shift summary ────────────────────────────────────────────
  const printShiftSummary = async (shiftId) => {
    try {
      const summary = await api.get(`/shifts/${shiftId}/summary`);
      const paperSize = invoiceTemplate?.paper_size || "80mm";
      const shiftHTML = generateShiftSummaryHTML(summary, paperSize);

      // Try QZ Tray first
      const receiptDest = printDestinations.find(
        (d) =>
          d.destination_type === "receipt" &&
          d.printer_type === "windows" &&
          d.windows_printer_name
      );

      if (receiptDest?.windows_printer_name) {
        try {
          const connected = await qzPrinter.connect();
          if (connected) {
            await qzPrinter.printRaw(
              receiptDest.windows_printer_name,
              shiftHTML
            );
            toast.success("Shift summary printed");
            return;
          }
        } catch (_) {
          // fall through
        }
      }

      // Browser fallback
      openPrintWindow(shiftHTML, "Shift Summary");
    } catch (_) {
      // Don't block the close flow
    }
  };

  // ─── Print order receipt (quick sale mode) ──────────────────────────
  const printOrderReceipt = async (order, orderData, { selectedTable, items, getSubtotal, getTax, getTotal, paymentMethod, promoDiscount, loyaltyDiscount }) => {
    if (!printerManager.hasConnectedPrinter()) return;

    try {
      const receiptPrinter = printerManager.getPrinterByType("receipt");
      if (!receiptPrinter) return;

      await printerManager.printReceipt(receiptPrinter.id, {
        businessName: currentOutlet?.property_name || "HostPoint POS",
        address: currentOutlet?.address || "",
        phone: currentOutlet?.phone || "",
        receiptNumber: order.order_number,
        date: new Date().toLocaleString(),
        tableNumber: selectedTable?.number,
        customerName: orderData.customer_name,
        servedBy: user?.full_name || user?.username,
        items: items.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        subtotal: getSubtotal(),
        tax: getTax(),
        discount: promoDiscount + loyaltyDiscount,
        total: getTotal() - promoDiscount - loyaltyDiscount,
        paymentMethod,
        footer:
          invoiceTemplate?.footer_text || "Thank you for your visit!",
      });
    } catch (_) {
      // printing is optional
    }
  };

  // ─── Print offline receipt via Bluetooth ────────────────────────────
  const printOfflineReceipt = async (offlineOrder) => {
    if (!printerManager.hasConnectedPrinter()) return;

    try {
      const receiptPrinter = printerManager.getPrinterByType("receipt");
      if (!receiptPrinter) return;

      await printerManager.printReceipt(receiptPrinter.id, {
        ...offlineOrder.receipt_data,
        isOffline: true,
      });
    } catch (_) {
      // printing is optional
    }
  };

  return {
    handleAutoPrint,
    handlePrintInvoice,
    printShiftSummary,
    printOrderReceipt,
    printOfflineReceipt,
    printReceiptBrowser,
    reserveBrowserReceiptWindow,
    closeReservedReceiptWindow,
    generatePrintHTMLLocal,
  };
}
